import { type GetStaticPropsContext, type GetStaticPaths } from "next";
import { getCategoriesSlug } from "~/lib/db/category";
import { type LoopPostReturnType } from "~/lib/db/loops";
import {
  getArticleByCategory,
  getPostsMetadata,
  getThumbnail,
} from "~/lib/db/posts";
import {
  getAllSpeciali,
  getNameSpeciali,
  type getNameSubSpecialiType,
  getParentSpeciali,
  getSubSpeciali,
} from "~/lib/db/speciali";
import { type ArticlePreviewType, ISSUES_TYPE } from "~/types";
import { postExcerpt } from "~/utils";
import TotalSpecial from "../../s/[speciali]/all";

const TotalSpecialPublicPage = ({
  infSpecial,
  categoryName,
  numberDescription,
  numeroName,
  numeroSlug,
  articles,
}) => {
  const restrictAccess =
    articles.filter((el) => el.meta._no_login_field == "1").length ==
    articles.length;

  return (
    <TotalSpecial
      infSpecial={infSpecial}
      categoryName={categoryName}
      numberDescription={numberDescription}
      numeroName={numeroName}
      numeroSlug={numeroSlug}
      articles={articles}
      restrictAccess={!restrictAccess}
    />
  );
};

export default TotalSpecialPublicPage;

export const getStaticPaths: GetStaticPaths<{
  speciali: string;
}> = async () => {
  const specialiSlug = await getCategoriesSlug(ISSUES_TYPE.speciali);

  return {
    paths: specialiSlug.map((speciali) => ({
      params: { speciali: speciali.slug },
    })), //indicates that no page needs be created at build time
    fallback: "blocking", //indicates the type of fallback
  };
};

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  if (!params || params.speciali == "speciali") return { notFound: true };
  const allSpeciali = (await getAllSpeciali()).filter(
    (speciali) => speciali.slug == params.speciali
  )[0];
  try {
    const speciali = await getNameSpeciali(allSpeciali.slug as string);
    if (!speciali) return { notFound: true };
    const [parent, subCategory, articleByCategory] = await Promise.all([
      getParentSpeciali(speciali.parent),
      getSubSpeciali(speciali.term_id),
      getArticleByCategory(speciali.term_id),
    ]);

    const articlesId = articleByCategory.map((el) => el.ID);

    const [metaData, thumbnail] = await Promise.all([
      articlesId.length > 0
        ? getPostsMetadata(articlesId)
        : Promise.resolve([]),
      articlesId.length > 0 ? getThumbnail(articlesId) : Promise.resolve([]),
    ]);

    const mapPostsForClient = (
      posts: LoopPostReturnType[]
    ): ArticlePreviewType[] =>
      posts.map(
        ({
          ID,
          post_title,
          post_name,
          post_excerpt,
          post_content,
          post_date,
        }) => ({
          post_name,
          post_title,
          post_content,
          post_excerpt:
            post_excerpt != ""
              ? postExcerpt(post_excerpt)
              : postExcerpt(
                  post_content
                    ?.replace(/<\/?[^>]+(>|$)/g, "")
                    ?.replace(/\[\/{0,1}et_pb_.*\]/g, "")
                ),
          meta: metaData[Number(ID)] != null ? metaData[Number(ID)] : null,
          thumbnail:
            thumbnail[Number(ID)]?.thumbnail != null
              ? thumbnail[Number(ID)]?.thumbnail
              : null,
          alt:
            thumbnail[Number(ID)]?.alt != null
              ? thumbnail[Number(ID)]?.alt
              : null,
          post_date: post_date.toISOString(),
        })
      );

    const mapSpecialiForClient = (
      posts: getNameSubSpecialiType[]
    ): getNameSubSpecialiType[] =>
      posts.map(({ slug, name, description }) => ({
        slug,
        name,
        description,
      }));

    return {
      props: {
        infSpecial: allSpeciali,
        categoryName: allSpeciali.name,
        category_slug: params.speciali,
        numeroSlug:
          allSpeciali.numeroSlug != null ? allSpeciali.numeroSlug : null,
        numeroName:
          allSpeciali.numeroName != null ? allSpeciali.numeroName : null,
        numberDescription:
          allSpeciali.numberDescription != null
            ? allSpeciali.numberDescription
            : null,
        parent_category_slug: parent?.slug || params.speciali,
        parent_category_name:
          parent?.name.replace("&amp;", " & ") ||
          speciali.name.replace("&amp;", " & "),
        subCategory: mapSpecialiForClient(subCategory),
        articles: mapPostsForClient(articleByCategory),
      },
      revalidate: 20 * 60,
    };
  } catch (error) {
    console.log("Error fetching static props for", params.speciali, error);
    return { notFound: true };
  }
};
